import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Signal,
  SimpleChanges,
  effect,
} from '@angular/core';
import { ISharedTab } from '@g99-product-web-ui/shared-ui';
import { closeIcon } from '@g99-product-web-ui/shared-util-lib';

import { NotificationReadStatusEnum } from '../../../enums/notification.enum';
import { NotificationFacade } from '../../../facades/notification.facade';
import {
  IGroupedNotifications,
  INotificationDto,
} from '../../../interfaces/notification.interface';

export type NotificationTab = 'unread' | 'all';

@Component({
  selector: 'g99-lib-notification-popup',
  templateUrl: './notification-popup.component.html',
  styleUrls: ['./notification-popup.component.scss'],
  standalone: false,
})
export class NotificationPopupComponent implements OnInit, OnChanges {
  @Input() public isOpen = false;
  @Output() public closed = new EventEmitter<void>();

  public groupedNotifications!: Signal<IGroupedNotifications>;
  public isLoadingNotifications!: Signal<boolean>;
  public hasMoreNotifications!: Signal<boolean>;
  public hasUnreadNotifications!: Signal<boolean>;
  public items!: Signal<INotificationDto>;
  public activeNotificationTab: NotificationTab = 'unread';

  protected readonly SKELETON_COUNT = 5;
  protected readonly skeletonPlaceholders: number[] = [
    ...new Array(this.SKELETON_COUNT).keys(),
  ];
  protected readonly skeletonTailPlaceholders: number[] = [0, 1];
  protected isMarkingAllRead = false;
  protected allNotifications: INotificationDto[] = [];
  protected closeIcon = closeIcon;

  constructor(private readonly _facade: NotificationFacade) {
    effect(() => {
      this.allNotifications = [
        ...this._facade.groupedNotifications().today,
        ...this._facade.groupedNotifications().older,
      ];
    });
  }

  public ngOnInit(): void {
    this.groupedNotifications = this._facade.groupedNotifications;
    this.isLoadingNotifications = this._facade.isLoading;
    this.hasMoreNotifications = this._facade.hasMore;
    this.hasUnreadNotifications = this._facade.hasUnread;
    this.allNotifications = [
      ...this.groupedNotifications().today,
      ...this.groupedNotifications().older,
    ];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const isOpenChange = changes['isOpen'];
    if (isOpenChange?.currentValue && !isOpenChange.previousValue) {
      this.activeNotificationTab = 'unread';
      this._facade.setTab('unread');
      this._facade.refresh();
    }
  }

  public get notificationTabs(): ISharedTab[] {
    return [
      { label: 'Unread', route: 'unread', count: this.unreadCount },
      { label: 'All', route: 'all' },
    ];
  }

  public onSharedTabClick(next: string): void {
    const normalized = (next || '').toLowerCase() as NotificationTab;
    if (normalized !== this.activeNotificationTab) {
      this.onTabChange(normalized);
    }
  }

  public get unreadCount(): number {
    const all = [
      ...this.groupedNotifications().today,
      ...this.groupedNotifications().older,
    ];
    return all.filter((n) => n.status === NotificationReadStatusEnum.UNREAD)
      .length;
  }

  public onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;
    if (
      !this.isLoadingNotifications() &&
      this.hasMoreNotifications() &&
      distanceFromBottom < 400
    ) {
      this._facade.loadMore();
    }
  }

  public onMarkAllRead(): void {
    this.isMarkingAllRead = true;
    this._facade.markAllRead().subscribe({
      complete: () => (this.isMarkingAllRead = false),
      error: () => (this.isMarkingAllRead = false),
    });
  }

  public onOpenItem(notificationId: number): void {
    this._facade.markOneRead(notificationId);
  }

  public onTabChange(nextTab: NotificationTab): void {
    if (this.activeNotificationTab !== nextTab) {
      this.activeNotificationTab = nextTab;
      this._facade.setTab(nextTab);
      this._facade.refresh();
    }
  }
}
